import React, { useState, useEffect, useMemo } from "react";

import { useAppDispatch, useAppSelector } from "../../redux/hooks/redux-hooks";
import { fetchBUs } from "../../redux/actions/BUActions";
import { displayDateFormart, isValidArr } from "../../utility/utils";
import EnhancedTable from "../table/Table";
import PageTitle from "../PageTitle";
import PageSearch from "../PageSearch";
import DialogBox from "../../components/dialog-box/DialogBox";
import BUForm from "./BUForm";
import Loader from "../../components/loader/Loader";
import { BUModel } from "../../models/BUModel";
import { fetchUsers } from "../../redux/actions/UserActions";

export type Mode = "add" | "edit";

type iData = {
  id: number;
  name: string;
  description: string;
  createdDate: string;
  buuser: string;
  isApprovalRequired: string;
  // status: string;
};

const BUPage = () => {
  const dispatch = useAppDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mode, setMode] = useState<Mode>("add");
  const [editData, setEditData] = useState<BUModel>();
  const [rootData, setRootData] = useState<iData[]>([]);
  const [filteredData, setFilteredData] = useState<iData[]>([]);

  const allBUs = useAppSelector((state) => state.bu.all_BUs);
  const allUsers = useAppSelector((state) => state.user.all_Users);
  const isBusFetching = useAppSelector((state) => state.bu.isBUsFetching);

  const columns = useMemo(() => {
    return [
      {
        id: "name",
        numeric: false,
        disablePadding: false,
        label: "Name",
      },
      {
        id: "description",
        numeric: false,
        disablePadding: false,
        label: "Description",
      },
      {
        id: "isApprovalRequired",
        numeric: false,
        disablePadding: false,
        label: "Approval Required",
      },
      {
        id: "buuser",
        numeric: false,
        disablePadding: false,
        label: "BU User",
      },
      {
        id: "createdDate",
        numeric: false,
        disablePadding: false,
        label: "Created Date",
        width: 150,
      },
      // {
      //   id: "status",
      //   numeric: false,
      //   disablePadding: false,
      //   label: "Status",
      // },
    ];
  }, []);

  //To fetch all bus
  useEffect(() => {
    dispatch(fetchBUs());
    dispatch(fetchUsers());
  }, [dispatch]);

  // To prepare the root data
  useEffect(() => {
    setRootData(() =>
      allBUs.map((bu, i) => {
        const user = bu?.buuserId
          ? allUsers.find((user) => user.userId === bu.buuserId)
          : "";
        const userName = user ? `${user.firstName} ${user.lastName}` : "N/A";
        return {
          id: bu.buid,
          name: bu.name,
          description: bu.description,
          createdDate: displayDateFormart(bu.createdDate),
          buuser: userName,
          isApprovalRequired: bu.isApprovalRequired ? "Yes" : "No",
          // status: bu.isActive ? "Active" : "Inactive",
        };
      })
    );
  }, [allBUs, allUsers]);

  // To set filtered data on change root data
  useEffect(() => {
    setFilteredData(rootData);
  }, [rootData]);

  const handleEdit = (id: string | number) => {
    const editBU = allBUs.find((rec) => rec.buid === id);
    setMode("edit");
    setEditData(editBU);
    setIsModalOpen(true);
  };

  const createHandler = () => {
    setMode("add");
    setEditData(undefined);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const searchHandler = (searchVal: string): void => {
    setFilteredData(() =>
      searchVal
        ? rootData.filter((info) =>
            Object.values(info).join("").toLowerCase().includes(searchVal)
          )
        : rootData
    );
  };

  return (
    <>
      {!isValidArr(allBUs) && isBusFetching ? <Loader /> : null}
      <PageTitle title="BUs" btnText="Add BU" onClick={createHandler} />
      <PageSearch placeholder="Search BUs" searchHandler={searchHandler} />
      <EnhancedTable
        columns={columns}
        data={filteredData}
        handleEdit={handleEdit}
      />
      {isModalOpen && (
        <DialogBox
          isModalOpen={isModalOpen}
          modalTitle={mode === "add" ? "Create BU" : "Edit BU"}
          handleModalClose={handleModalClose}
        >
          <BUForm
            mode={mode}
            data={editData}
            handleModalClose={handleModalClose}
          />
        </DialogBox>
      )}
    </>
  );
};

export default BUPage;
